"use client";

import type { FC, ReactNode } from "react";
import { useEffect, useId, useMemo } from "react";
import { PhotoIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { cn } from "@/lib/cn";
import { FieldLabel, FieldMessage, type FieldState } from "./field-chrome";
import { useFileDrop } from "./use-file-drop";

type ImageDropzoneProps = {
  className?: string;
  label?: ReactNode;
  message?: ReactNode;
  state?: FieldState;
  required?: boolean;
  accept?: string[];
  minSize?: number;
  maxSize?: number;
  value?: File | null;
  defaultValue?: File | null;
  /** An already-stored image to show until a new file is chosen — e.g. the photo on a saved draft. */
  previewUrl?: string | null;
  /** Replaces the default filename + accepted-formats block with one line of the caller's copy.
   *  A chosen file still wins, so the user always sees what they picked. */
  prompt?: ReactNode;
  onValueChange?: (file: File | null) => void;
  disabled?: boolean;
  id?: string;
};

export const ImageDropzone: FC<ImageDropzoneProps> = ({
  className,
  label,
  message,
  state = "default",
  required,
  accept,
  minSize,
  maxSize,
  value,
  previewUrl: storedPreviewUrl,
  prompt,
  onValueChange,
  disabled,
  id,
}) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const { file, setFile, dragOver, localError, inputProps, dropProps, formatSize } = useFileDrop({
    accept,
    minSize,
    maxSize,
    value,
    onValueChange,
  });
  const objectUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  /* The stored image is the fallback, never the override — a freshly picked file must win. */
  const previewUrl = objectUrl ?? storedPreviewUrl ?? null;

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const effectiveState: FieldState = localError ? "error" : state;
  const effectiveMessage = localError ?? message;

  return (
    <div className="flex w-full flex-col gap-8">
      <FieldLabel htmlFor={inputId} state={effectiveState} required={required}>
        {label}
      </FieldLabel>
      <label
        htmlFor={inputId}
        {...dropProps}
        className={cn(
          "flex w-full items-center gap-16 rounded-medium border border-dashed p-16 transition-colors",
          disabled ? "pointer-events-none cursor-not-allowed opacity-50" : "cursor-pointer",
          dragOver
            ? "border-brand-blue bg-brand-blue/5"
            : effectiveState === "error"
              ? "border-brand-red"
              : "border-neutral-mid-grey",
          className,
        )}
      >
        <input id={inputId} disabled={disabled} {...inputProps} />
        {previewUrl ? (
          // eslint-disable-next-line @next/next/no-img-element -- object URL preview, not an optimizable remote asset
          <img src={previewUrl} alt="" className="size-67 shrink-0 rounded-regular object-cover" />
        ) : (
          <PhotoIcon className="size-67 shrink-0 text-neutral-mid-grey" />
        )}
        {prompt ? (
          <span className="min-w-0 flex-1 truncate text-18 font-medium text-neutral-black">{file ? file.name : prompt}</span>
        ) : (
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            <span className="truncate text-16 text-neutral-black">
              {file ? file.name : storedPreviewUrl ? "Click to replace the uploaded photo" : "Click to upload or drag and drop"}
            </span>
            <span className="text-14 text-neutral-grey">
              {accept && accept.length > 0 ? accept.map((mime) => mime.split("/")[1]?.toUpperCase()).join(", ") : "Any image"}
              {maxSize ? ` · up to ${formatSize(maxSize)}` : ""}
            </span>
          </div>
        )}
        {file && (
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setFile(null);
            }}
            className="shrink-0 cursor-pointer rounded-small p-8 text-neutral-grey hover:bg-neutral-black/5 hover:text-neutral-black"
          >
            <XMarkIcon className="size-20" />
          </button>
        )}
      </label>
      <FieldMessage state={effectiveState}>{effectiveMessage}</FieldMessage>
    </div>
  );
};
